import { format, addMonths, parse, isAfter, isSameMonth } from 'date-fns';
import {
  createFilterOptions,
  Link,
  Typography,
} from '@mui/material';

import { getAccountFullDetails } from '../Helpers/dataApi';

export const Copyright = (props) => {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {'Copyright © '}
      <Link color="inherit" href="https://bridgefirstforecasting.com/">
        BridgeFirst Forecasting
      </Link>{' '}
      {new Date().getFullYear()}
    </Typography>
  );
}

/**
 * Parse a timestamp string from the backend into a Date object in the user's
 * local timezone.
 *
 * The backend serializes datetime columns without a timezone suffix
 * (e.g. "2024-01-15T10:30:00").  Per the ECMAScript spec, ISO 8601 strings
 * without timezone info are parsed as *local* time, which is wrong when the
 * server stores UTC.  This helper appends 'Z' when no timezone indicator is
 * present so that `new Date()` always interprets the value as UTC and then
 * displays it in the user's browser timezone.
 *
 * Strings that already carry an offset ('+00:00', '-05:00', 'Z', etc.) are
 * passed through unchanged.
 *
 * @param {string|null|undefined} timestamp - Raw timestamp from the API
 * @returns {Date|null} - A Date in the user's local timezone, or null if invalid
 */
export const parseUtcTimestamp = (timestamp) => {
  if (!timestamp) return null;
  // Already has timezone info — leave it alone.
  // Matches: trailing 'Z', '+HH:MM', or '-HH:MM'
  const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(timestamp);
  const normalized = hasTimezone ? timestamp : `${timestamp}Z`;
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Safely parse a date string from the backend (yyyy-MM-dd format)
 * Backend stores dates as the LAST day of the PREVIOUS month, so we add 1 day
 * to get the actual month being represented.
 * 
 * @param {String} dateString - Date string in 'yyyy-MM-dd' format
 * @param {String} outputFormat - Desired output format (default: 'MMM yyyy')
 * @returns {String} Formatted date string
 */
export const formatBackendDate = (dateString, outputFormat = 'MMM yyyy') => {
  if (!dateString) return '';
  
  try {
    // Parse the date string
    const parsedDate = parse(dateString, 'yyyy-MM-dd', new Date());
    
    // Backend stores dates as the LAST day of the PREVIOUS month
    // Add 1 day to get the actual month being represented
    parsedDate.setDate(parsedDate.getDate() + 1);
    
    return format(parsedDate, outputFormat);
  } catch (error) {
    console.error('Error formatting backend date:', dateString, error);
    return dateString;
  }
};

/**
 * Get month and year from a backend date string for use with month pickers
 * Month picker uses 1-12 for months (not 0-indexed)
 * 
 * @param {String} dateString - Date string in 'yyyy-MM-dd' format
 * @returns {Object} Object with { month: number (1-12), year: number }
 */
export const getMonthYearFromBackendDate = (dateString) => {
  if (!dateString) return null;
  
  try {
    const parsedDate = parse(dateString, 'yyyy-MM-dd', new Date());
    // Backend stores dates as the LAST day of the PREVIOUS month
    parsedDate.setDate(parsedDate.getDate() + 1);
    
    return {
      month: parsedDate.getMonth() + 1, // Convert 0-indexed to 1-indexed
      year: parsedDate.getFullYear()
    };
  } catch (error) {
    console.error('Error parsing backend date:', dateString, error);
    return null;
  }
};

/**
 * Converts a number to an Excel-style column value
 * @param {Number} number 
 * @returns Excel-style column value
 */
export const numberToColumn = (number) => {
  let letters = "ABCDEFGHIJKLMNOPQRSTUVWYXZ";
  let output = "";
  while(number) {
    output = letters[(number - 1) % letters.length] + output;
    number = Math.floor(number / letters.length);
  }
  return output;
}

/**
 * @param {String} column An excel-style column designation
 * @returns The next excel-style column value after the one supplied
 */
export const getNextExcelColumn = (column) => {
  const getNextLetter = letter => {
    if (letter === 'Z') {
      return 'A';
    } else {
      return String.fromCharCode(letter.charCodeAt(0) + 1);
    }
  };

  const reverseColumn = column.split('').reverse().join('');
  let carry = true;
  let nextColumn = '';

  for (let i = 0; i < reverseColumn.length; i++) {
    if (carry) {
      const currentLetter = reverseColumn[i];
      if (i === 0) {
        nextColumn += getNextLetter(currentLetter);
        carry = currentLetter === 'Z';
      } else {
        if (currentLetter === 'Z') {
          nextColumn += 'A';
        } else {
          nextColumn += getNextLetter(currentLetter);
          carry = false;
        }
      }
    } else {
      nextColumn += reverseColumn.slice(i);
      break;
    }
  }

  if (carry) {
    nextColumn += 'A';
  }

  return nextColumn.split('').reverse().join('');
}

/**
 * Retrieves a locally stored DB object
 * @deprecated This function is no longer used as local IndexedDB storage has been removed
 * @param {String} item Specified DB object to query from a local Dexie instance
 * @returns Object stored value
 */
export const getStorageItem = async(item) => {
  console.warn('getStorageItem is deprecated and no longer functional');
  return false;
}

/**
 * Formats a number for UI display
 * @param {Number} x Number to be formatted
 * @returns A human-readable formatted number
 */
export const numberWithCommas = (x) => {
  if(x < 0){
    x = `(${Math.abs(x)})`;
  }
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Capitalizes the first letter in a string
 * @param {String} string Word to be capitalized
 * @returns String witht he first letter capitalized
 */
export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * 
 * @param {Number} i Indicates user type
 * @returns Human-readable label of the user type
 */
export const userTypes = (i) => {
  const userType = {
    0: 'Super User', // Can access all organizations and projects, modify all user account permissions
    1: 'Organization Admin', // Can access all projects, modify all user account permissions in an organization
    2: 'Contributor', // Can access projects granted by an Organization Admin. Cannot modify user permissions
    3: 'Read-only', // Read-only
  }
  return userType[i];
}

/**
 * Determines if the value is, or can be converted to, a date
 * @param {String} date Value to be evlauted
 * @returns Boolean
 */
export const isDate = (date) => {
  return date instanceof Date && !isNaN(date.valueOf());
}

/**
 * 
 * @param {String} start First month of a date range
 * @param {String} end Last month of a date range
 * @returns Array with all months from start to end, format MMM YYYY
 */
export const getMonthsBetweenDates = (start, end) => {
  // Parse dates - handle both 'MMM yyyy' and 'yyyy-MM-dd' formats
  let dateStart, dateEnd;
  
  // Check if the date is in 'yyyy-MM-dd' format (contains dashes and starts with year)
  if (start && start.match(/^\d{4}-\d{2}-\d{2}$/)) {
    dateStart = parse(start, 'yyyy-MM-dd', new Date());
    // IMPORTANT: Backend stores dates as the LAST day of the PREVIOUS month
    // Add 1 day to get the actual month being represented
    dateStart.setDate(dateStart.getDate() + 1);
  } else {
    // Assume 'MMM yyyy' format
    dateStart = parse(start, 'MMM yyyy', new Date());
  }
  
  if (end && end.match(/^\d{4}-\d{2}-\d{2}$/)) {
    dateEnd = parse(end, 'yyyy-MM-dd', new Date());
    // IMPORTANT: Backend stores dates as the LAST day of the PREVIOUS month
    // Add 1 day to get the actual month being represented
    dateEnd.setDate(dateEnd.getDate() + 1);
  } else {
    // Assume 'MMM yyyy' format
    dateEnd = parse(end, 'MMM yyyy', new Date());
  }
  
  if (dateStart && dateEnd && !isNaN(dateStart.getTime()) && !isNaN(dateEnd.getTime())) {
    const timeValues = [];
    let current = dateStart;

    while (isAfter(dateEnd, current) || isSameMonth(dateEnd, current)) {
      timeValues.push(format(current, 'MMM yyyy'));
      current = addMonths(current, 1);
    }
    return timeValues;
  }
  return null;
}

/**
 * Remove duplicate entries from an array
 * @param {Array} arr 
 * @returns Array with duplicate entries removed
 */
export const removeDuplicates = (arr) => {
  return arr.filter((item,
      index) => arr.indexOf(item) === index);
}

/**
 * Generates an array of GL keys from selections from a Material UI Autocomplete component
 * @param {Array} selectedAccounts Array of selections from a Material UI Autocomplete component
 * @returns Array of GL Keys representing accounts selected
 */
export const getGlKeysFromSelect = (selectedAccounts) => {
  let accountGlKeyArray = selectedAccounts.map((account) => {
    return account.glKey;
  });
  return accountGlKeyArray;
}

/**
 * Returns details of an account, specified by GL Key, given the treeData of the active project
 * @param {String} glKey Unique key for each account describing its Enity, Department, and Account IDs
 * @param {Object} treeData Represents all Entities, Departments, and Accounts accociated with the project
 * @param {Boolean} isCalculatedAccount
 * @returns {Object} Details of a requested account
 */
export const getAccountDetails = (glKey, treeData) => {
  let accountDetails;
  if(glKey.length < 1){
    console.error("Invalid glKey:", glKey);
    return false;
  }
  if(!treeData || Object.keys(treeData).length === 0){
    console.error("Invalid treeData:", treeData)
    return false;
  }

  if(glKey.length > 0 && Object.keys(treeData).length > 0){
    const ids = glKey.split("-");
    if(ids.length === 3){
      if(treeData.entities[ids[0]].departments[ids[1]].accounts[ids[2]]){
        accountDetails = treeData.entities[ids[0]].departments[ids[1]].accounts[ids[2]];
      }
      else{
        console.error("GL Key did not match with treeData", glKey, treeData);
        return false;
      }
    }
    else{
      console.error("Invalid GL Key:", glKey);
      return false;
    }

    if(Object.keys(accountDetails).length === 0 || typeof accountDetails === "undefined"){
      console.error("Could not retreive account details", "glKey:", glKey, "treeData:", treeData);
      return false;
    }
  }
  else{
    console.error("Invalid data supplied", "glKey:", glKey, "treeData:", treeData);
    return false;
  }
  
  return accountDetails;
}

/***
 * details of a calculated account, specified by GL Key, given the treeData of the active project
 * @param {String} glKey
 * @param {Object} activeProjectAttributes
 * @returns {Object} Details of a requested calculated account
 */
export const getCalculatedAccountDetails = (glKey, agGridData) => {
  const accountIndex = agGridData.findIndex(acc => acc.glKey === glKey);
  const accountDetails = agGridData[accountIndex];
  // console.log('agGridData', agGridData);
  // console.log(accountDetails);
  if(accountIndex > -1){
    return accountDetails;
  }
  else{
    console.error("Could not retreive account details", "glKey:", glKey, "agGridData:", agGridData);
    return false;
  }
  
}

/**
 * Creates a filter for a MUI Autocomplete for selecting accounts
 */
export const accountFilter = createFilterOptions({
  matchFrom: 'any',
  stringify: (option) => `${option.label} ${option.parentAccount}`,
});


export const newGetAccountDetails = async (projectId, glKey, accessToken) => {
  const glKey_to_array = glKey.split("-");

  const entity_id = glKey_to_array[0];
  const department_id = glKey_to_array[1];
  const account_id = glKey_to_array[2];

  try {
    const response = await getAccountFullDetails(
      projectId,
      entity_id,
      department_id,
      account_id,
      accessToken
    );

    const accountsMetadata = [];

    if (response) {
      const accounts = response.data;
      // Process the accounts object to extract required fields
      Object.entries(accounts).forEach(([entityName, departmentsObj]) => {
        Object.entries(departmentsObj).forEach(([departmentName, accountsArray]) => {
          accountsArray.forEach((account) => {
            accountsMetadata.push(account);
          });
        });
      });
    }

    return accountsMetadata;
  } catch (err) {
    // console.log("Got an error trying to fetch project accounts.");
    console.error(err);
    // Optionally, you can rethrow the error or return a default value
    throw err; // or return [];
  }
};

export const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
